/**
 * Pure helpers for exhibit citations in drafts.
 *
 * A citation in a docx is a content control tagged `cbx:<nodeId>` or
 * `cbx:<nodeId>;p=<page>` (see `docx_engine/src/citations.js`). Its visible
 * text is regenerated from the case's current exhibit labels whenever the draft
 * is rendered, so the UI reads labels live from the filesystem rather than
 * trusting what the document says. In chat and agent text the same citation
 * travels as the marker `⟦cite:<nodeId>[@p<page>][|label]⟧`.
 */
import { labelStyle } from "../caseFiles/exhibitLabels";
import type { ExhibitScheme, FsNode } from "../caseFiles/types";

export type CitationRef = {
  nodeId: string;
  /** 1-based pinned page, or null for the whole exhibit. */
  page: number | null;
};

export const CITATION_TAG_PREFIX = "cbx:";

/**
 * The legacy 2.10 engine paints one of these per content control; ours carry a
 * `cbx:` tag. The eigenpal engine keeps controls in the model instead; see
 * `locator.ts` for how citations are found there.
 */
export const CITATION_CHROME_SELECTOR =
  '.docx-content-control-chrome[data-tag^="cbx:"]';
export const CITATION_FRAGMENT_SELECTOR = ".docx-content-control-boundary";

const DEFAULT_PREFIX = "Exhibit ";

export const REMOVED_EXHIBIT_TEXT = "Exhibit removed from packet";

const normalizePage = (value: string | number | null | undefined) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : null;
};

/** `cbx:abc;p=3` → `{nodeId: "abc", page: 3}`; anything else → null. */
export const parseCitationTag = (
  tag: string | null | undefined
): CitationRef | null => {
  if (!tag || !tag.startsWith(CITATION_TAG_PREFIX)) return null;
  const body = tag.slice(CITATION_TAG_PREFIX.length);
  const [nodeId, ...params] = body.split(";");
  if (!nodeId) return null;
  let page: number | null = null;
  for (const param of params) {
    const [key, value] = param.split("=");
    if (key === "p") page = normalizePage(value);
  }
  return { nodeId, page };
};

/** The tag the engine writes for a citation. */
export const citationTag = ({ nodeId, page }: CitationRef): string =>
  `${CITATION_TAG_PREFIX}${nodeId}${page ? `;p=${page}` : ""}`;

/** Stable identity for a citation regardless of which content control carries it. */
export const citationKey = ({ nodeId, page }: CitationRef): string =>
  `${nodeId}@${page ?? ""}`;

/**
 * How this case words a citation in prose; mirrors `citation_prefix` in the
 * backend. Punctuation is a list-style concern and never appears inline.
 */
export const citationPrefix = (scheme: ExhibitScheme | null | undefined) => {
  if (!scheme) return DEFAULT_PREFIX;
  const style = labelStyle(scheme);
  return style.prefix ? `${style.prefix} ` : DEFAULT_PREFIX;
};

/**
 * The text a citation reads as today: "Exhibit B.2". A pinned page stays in
 * the tag (the hover card and the click show it) and is never printed, as in
 * the engine's `citationText`. `null` when the cited node has no label
 * (removed, excluded, or unknown).
 */
export const citationLabel = (
  labels: Record<string, string | null> | null | undefined,
  scheme: ExhibitScheme | null | undefined,
  ref: CitationRef
): string | null => {
  const label = labels?.[ref.nodeId];
  if (!label) return null;
  return `${citationPrefix(scheme)}${label}`;
};

/** "p. 3" for a pinned citation, empty otherwise. */
export const pinciteText = (page: number | null | undefined) =>
  page ? `p. ${page}` : "";

export const nodeById = (
  nodes: readonly FsNode[] | null | undefined,
  nodeId: string
): FsNode | null =>
  nodes?.find((node) => node.id === nodeId && !node.is_deleted) ?? null;

// --- Inline markers -------------------------------------------------------

const MARKER =
  /\u27E6cite:([^\u27E7|@\s]+)(?:@p(\d+))?(?:\|([^\u27E7]*))?\u27E7/g;

export type CitationToken =
  | { kind: "text"; text: string }
  | {
      kind: "citation";
      nodeId: string;
      page: number | null;
      label: string | null;
    };

export const hasCitationMarkers = (text: string | null | undefined) =>
  typeof text === "string" && text.includes("\u27E6cite:");

/**
 * Split `⟦cite:node@p3|Exhibit B.2 at 3⟧` markers out of a string. The label
 * part is what the document read when the text was captured; callers should
 * prefer the live label when they have one.
 */
export const splitCitationTokens = (
  text: string | null | undefined
): CitationToken[] => {
  const value = text ?? "";
  if (!hasCitationMarkers(value))
    return value ? [{ kind: "text", text: value }] : [];
  const out: CitationToken[] = [];
  let cursor = 0;
  MARKER.lastIndex = 0;
  for (const match of value.matchAll(MARKER)) {
    const start = match.index ?? 0;
    if (start > cursor)
      out.push({ kind: "text", text: value.slice(cursor, start) });
    out.push({
      kind: "citation",
      nodeId: match[1],
      page: match[2] ? normalizePage(match[2]) : null,
      label: match[3] ? match[3] : null
    });
    cursor = start + match[0].length;
  }
  if (cursor < value.length)
    out.push({ kind: "text", text: value.slice(cursor) });
  return out;
};

/**
 * Markers replaced by the text they read as (the label part, or a placeholder
 * when the marker carries none). What to search for in the editor, and what
 * to show where a chip cannot be rendered.
 */
export const stripCitationMarkers = (text: string | null | undefined): string =>
  splitCitationTokens(text)
    .map((token) =>
      token.kind === "text" ? token.text : token.label ?? "Exhibit ?"
    )
    .join("");

/** Node ids cited by any markers in `text`, deduplicated, in order. */
export const citedNodeIds = (text: string | null | undefined): string[] => {
  const seen = new Set<string>();
  for (const token of splitCitationTokens(text)) {
    if (token.kind === "citation") seen.add(token.nodeId);
  }
  return [...seen];
};

/**
 * A fingerprint of how the cited exhibits are labelled right now. The draft
 * pane compares it across filesystem refreshes to notice a renumbering that
 * affects the open draft (and only that draft).
 */
export const citationSignature = (
  labels: Record<string, string | null> | null | undefined,
  scheme: ExhibitScheme | null | undefined,
  nodeIds: Iterable<string>
): string => {
  const prefix = citationPrefix(scheme);
  const parts = [...new Set(nodeIds)]
    .sort()
    .map((id) => `${id}=${labels?.[id] ?? ""}`);
  return `${prefix}|${parts.join(",")}`;
};
